import React, { useEffect, useMemo, useState } from 'react';
import { ToastStack } from './components/ToastStack.jsx';
import { SkeletonForm } from './components/SkeletonForm.jsx';
import { useToasts } from './hooks/useToasts.js';
import { SECTIONS } from './constants.jsx';
import { NameGreetingPanel } from './panels/NameGreetingPanel.jsx';
import { TonePanel } from './panels/TonePanel.jsx';
import { ModelPanel } from './panels/ModelPanel.jsx';
import { LeadCapturePanel } from './panels/LeadCapturePanel.jsx';
import { PublishDialog } from './components/PublishDialog.jsx';
import { PreviewChat } from './components/PreviewChat.jsx';
import { LanguagePanel } from './panels/LanguagePanel.jsx';
import { PersonalityPanel } from './panels/PersonalityPanel.jsx';
import { FallbackPanel } from './panels/FallbackPanel.jsx';
import { HandoffPanel } from './panels/HandoffPanel.jsx';
import { ProhibitedTopicsPanel } from './panels/ProhibitedTopicsPanel.jsx';
import { ResponseBoundaryPanel } from './panels/ResponseBoundaryPanel.jsx';
import { ResponsiveSettingsPanel } from './panels/ResponsiveSettingsPanel.jsx';
import { ResponseStylePanel } from './panels/ResponseStylePanel.jsx';
import useActiveBot from '../hooks/useActiveBot';
import BotSwitcher from '../components/common/BotSwitcher';
import { updateBot as saveBotToApi } from '../api/services/botService';
import './botConfiguration.css';

const DEFAULT_BOT = {
  name: 'ChatBizz Assistant', greeting: 'Hi! How can I help you today?', tone: 'friendly',
  primaryLanguage: 'en', supportedLanguages: ['en'], instructions: '',
  fallbackMessage: 'I’m sorry, I couldn’t find a reliable answer. Would you like me to connect you with a person?',
  handoffEnabled: true, prohibitedTopics: [], prohibitedTopicAction: 'fallback', responseBoundaryEnabled: true,
  responseBoundaryScope: '', responseBoundaryMessage: '', responseBoundaryAction: 'redirect', responseStyle: 'conversational',
  responseLength: 'balanced', responseFormat: 'paragraphs', responseEmoji: false, customProhibitedTopics: [], status: 'draft',
  handoff: { enabled: true, triggers: { explicitRequest: true, lowConfidence: false, keywordMatch: false }, sensitivity: '', keywords: [], message: '', offHoursMode: 'queue', offlineMessage: '' },
};
// A bot record keeps the Bot Management fields (name, greeting, status) at the top level and the
// Bot Configuration fields under `config`; this panel set edits one flat view of both.
function toView(serverBot) {
  return { ...DEFAULT_BOT, ...(serverBot.config || {}), model: serverBot.model, name: serverBot.name, greeting: serverBot.greeting, status: serverBot.status === 'active' ? 'published' : 'draft' };
}

export default function BotConfigurationPage() {
  const [initialLoading,setInitialLoading]=useState(true);
  const [active,setActive]=useState(()=>new URLSearchParams(window.location.search).get('section')||'name');
  const [mockErrorOn,setMockErrorOn]=useState(false);
  const [,setMobileOpen]=useState(false);
  const {status:botsStatus,bots,bot:serverBot,botId,setBotId,replaceBot,reload}=useActiveBot();
  const bot=useMemo(()=>serverBot?toView(serverBot):DEFAULT_BOT,[serverBot]);
  const {toasts,push}=useToasts();
  const [publishAction,setPublishAction]=useState(null); // 'publish' | 'unpublish'
  const [publishing,setPublishing]=useState(false);
  const [previewMode,setPreviewMode]=useState('look'); // 'look' | 'try'
  useEffect(()=>{const t=setTimeout(()=>setInitialLoading(false),350);return()=>clearTimeout(t)},[]);
  const updateBot=patch=>{
    if(!botId||!serverBot)return;
    const {name,greeting,status,model,...rest}=patch;
    const body={};
    if(name!==undefined)body.name=name;
    if(greeting!==undefined)body.greeting=greeting;
    if(status!==undefined)body.status=status==='published'?'active':'inactive';
    if(model!==undefined)body.model=model;
    if(Object.keys(rest).length)body.config=rest;
    replaceBot({...serverBot,...(body.name!==undefined?{name:body.name}:{}),...(body.greeting!==undefined?{greeting:body.greeting}:{}),...(body.status!==undefined?{status:body.status}:{}),...(body.model!==undefined?{model:body.model}:{}),config:{...(serverBot.config||{}),...rest}});
    saveBotToApi(botId,body).then(replaceBot).catch(err=>{push('error',err.message||'Could not save changes.');reload();});
  };
  const chooseSection=id=>{setActive(id);setMobileOpen(false);const u=new URL(window.location.href);u.searchParams.set('section',id);window.history.replaceState({},'',u)};
  const panels={
    name:<NameGreetingPanel bot={bot} updateBot={updateBot} mockErrorOn={mockErrorOn} notify={push}/>,
    'lead-capture':<LeadCapturePanel bot={bot} updateBot={updateBot} notify={push}/>,
    model:<ModelPanel bot={bot} updateBot={updateBot} notify={push}/>,
    tone:<TonePanel bot={bot} updateBot={updateBot} mockErrorOn={mockErrorOn} notify={push}/>,
    language:<LanguagePanel bot={bot} updateBot={updateBot} mockErrorOn={mockErrorOn} notify={push}/>,
    personality:<PersonalityPanel bot={bot} updateBot={updateBot} mockErrorOn={mockErrorOn} notify={push}/>,
    fallback:<FallbackPanel bot={bot} updateBot={updateBot} mockErrorOn={mockErrorOn} notify={push}/>,
    handoff:<HandoffPanel bot={bot} updateBot={updateBot} mockErrorOn={mockErrorOn} notify={push}/>,
    prohibited:<ProhibitedTopicsPanel bot={bot} updateBot={updateBot} mockErrorOn={mockErrorOn} notify={push}/>,
    'response-boundary':<ResponseBoundaryPanel bot={bot} updateBot={updateBot} mockErrorOn={mockErrorOn} notify={push}/>,
    'response-style':<ResponseStylePanel bot={bot} updateBot={updateBot} mockErrorOn={mockErrorOn} notify={push}/>,
    'responsive-settings':<ResponsiveSettingsPanel mockErrorOn={mockErrorOn}/>
  };
  const section=SECTIONS.find(s=>s.id===active)||SECTIONS[0];
  const currentTitle = section.label;
  const appearance = serverBot?.appearance || {};
  const livePreview = {
    primary: appearance.primary || '#1677ff',
    secondary: appearance.secondary || '#00c2ff',
    font: appearance.font || 'Poppins',
    botName: appearance.botName || bot.name || 'ChatBizz Assistant',
    welcome: bot.greeting || appearance.welcome || 'Hi! How can I help you today?',
    avatar: appearance.avatar || appearance.logo || ''
  };

  return <div className="bc-root bc-integrated-page">
    <div className="bc-heading">
      <div>
        <div className="bc-eyebrow">SETTINGS / WORKSPACE</div>
        <h1>Bot Configuration</h1>
        <p>Configure the bot inside the same Authentication Dashboard — no second dashboard.</p>
      </div>
      <span className="bc-status"><i/> Integrated</span>
    </div>

    <BotSwitcher bots={bots} botId={botId} onChange={setBotId}/>

    <div className="bc-task-strip">
      {SECTIONS.map((s) => <button key={s.id} className={active === s.id ? 'active' : ''} onClick={() => chooseSection(s.id)}>{s.label}</button>)}
    </div>

    <div className="bc-current">
      <span>Current task</span>
      <strong>{currentTitle}</strong>
      <span className={'bc-draft-status ' + (bot.status === 'published' ? 'published' : '')}>{bot.status === 'published' ? 'Published' : 'Draft'}</span>
      <button className="bc-secondary-btn" disabled={!serverBot} onClick={() => setPublishAction(bot.status==='published'?'unpublish':'publish')}>{bot.status==='published'?'Unpublish':'Publish bot'}</button>
      <button className={'bc-secondary-btn' + (mockErrorOn ? ' error-on' : '')} onClick={() => setMockErrorOn(v => !v)}>{mockErrorOn ? 'API error: ON' : 'Simulate API error'}</button>
    </div>

    <div className="bc-integrated-grid">
      <section className="bc-panel-area">
        {initialLoading||botsStatus==='loading' ? <SkeletonForm/> : botsStatus==='empty' ? <p className="text-muted">No bots yet — create one in Bot Management first.</p> : botsStatus==='error' ? <p className="text-danger">Could not load your bots. Please refresh.</p> : <React.Fragment key={botId}>{panels[active]}</React.Fragment>}
      </section>
      <section className="bc-preview-card">
        <div className="bc-preview-head"><span className="bc-browser-dots">● ● ●</span><span>{previewMode==='try'?'Try your bot':'Live customer preview'}</span><b>{previewMode==='try'?'Test':'Live'}</b></div>
        <div className="bc-preview-tabs" role="tablist" aria-label="Preview mode">
          <button type="button" role="tab" aria-selected={previewMode==='look'} className={previewMode==='look'?'on':''} onClick={()=>setPreviewMode('look')}>Appearance</button>
          <button type="button" role="tab" aria-selected={previewMode==='try'} className={previewMode==='try'?'on':''} onClick={()=>setPreviewMode('try')} disabled={!serverBot}>Try it</button>
        </div>
        {previewMode==='try' && serverBot ? <PreviewChat botId={botId} bot={bot} primary={livePreview.primary}/> : <div className="bc-preview-site" style={{fontFamily:livePreview.font}}>
          <div className="bc-preview-hero">Smarter Support<br/><strong>Happier Customers!</strong></div>
          <div className="bc-chat-window">
            <div className="bc-chat-head" style={{background:livePreview.primary}}>
              {livePreview.avatar ? <img src={livePreview.avatar} alt="Assistant avatar"/> : <div className="bc-avatar">✦</div>}
              <div><strong>{livePreview.botName}</strong><small>● Online</small></div>
            </div>
            <div className="bc-chat-body"><div className="bc-bubble">{livePreview.welcome}</div><div className="bc-user-bubble" style={{background:livePreview.primary}}>I want to know about your services.</div></div>
            <div className="bc-chat-input"><span>Type your message...</span><button style={{background:livePreview.primary}}>➤</button></div>
          </div>
          <div className="bc-launcher" style={{background:`linear-gradient(135deg,${livePreview.primary},${livePreview.secondary})`}}>✦</div>
        </div>}
      </section>
    </div>
    {publishAction&&serverBot&&<PublishDialog bot={bot} action={publishAction} busy={publishing} onCancel={()=>setPublishAction(null)} onConfirm={async()=>{
      setPublishing(true);
      try{const updated=await saveBotToApi(botId,{status:publishAction==='publish'?'active':'inactive'});replaceBot(updated);push('success',publishAction==='publish'?'Bot published — visitors can now chat with it.':'Bot unpublished.');setPublishAction(null);}
      catch(err){push('error',err.message||'Could not change the publish state.');}
      finally{setPublishing(false);}
    }}/>}
    <ToastStack toasts={toasts}/>
  </div>;

}
